import { injectable } from 'inversify';
import { EntityServiceBase } from '@/bases/services/EntityServiceBase';
import {
  Appointment,
  NotificationTemplateNotificationType,
  ShowCallInfoResponse,
  ShowCallResponse,
  CallResponse,
  Branding
} from '@/models';
import { AxiosPromise } from 'axios';

@injectable()
export class AppointmentService extends EntityServiceBase<Appointment> {
  public constructor() {
    super();
    this.EntityName = 'Appointment';
  }

  public GetAppointmentByKey(
    reference: string
  ): AxiosPromise<Appointment> {
    return this.Get<Appointment>(
      `${this.BaseUrl}/GetAppointmentByKey?ref=${encodeURIComponent(
        reference
      )}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public CancelAppointment(
    reference: string | null
  ): AxiosPromise<ShowCallInfoResponse> {
    return this.Post<ShowCallInfoResponse>(
      `${this.BaseUrl}/CancelAppointment?ref=${reference}`,
      null,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public ConfirmAppointment(
    reference: string | null
  ): AxiosPromise<ShowCallInfoResponse> {
    return this.Post<ShowCallInfoResponse>(
      `${this.BaseUrl}/ConfirmAppointment?ref=${reference}`,
      null,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public GetAppointmentCalendarICSURL(
    reference: string | null
  ): AxiosPromise<string> {
    return this.Get<string>(
      `${this.BaseUrl}/GetAppointmentCalendarICSURL?ref=${reference}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public GetAppointmentCalendarGoogleURL(
    reference: string | null
  ): AxiosPromise<string> {
    return this.Get<string>(
      `${this.BaseUrl}/GetAppointmentCalendarGoogleURL?ref=${reference}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public GetAppointmentCalendarOutlookURL(
    reference: string | null
  ): AxiosPromise<string> {
    return this.Get<string>(
      `${this.BaseUrl}/GetAppointmentCalendarOutlookURL?ref=${reference}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    )
  }

  public GetAppointmentCalendarOffice365URL(
    reference: string | null
  ): AxiosPromise<string> {
    return this.Get<string>(
      `${this.BaseUrl}/GetAppointmentCalendarOffice365URL?ref=${reference}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    )
  }

  public GetSuggestions(
    reference: string
  ): AxiosPromise<CallResponse> {
    return this.Get<CallResponse>(
      `${this.BaseUrl}/GetSuggestions?ref=${reference}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public ConfirmSuggestion(
    reference: string,
    suggestedDate: Date
  ): AxiosPromise<CallResponse> {
    return this.Post(
      `${this.BaseUrl}/ConfirmSuggestion?ref=${reference}&suggestedDate=${suggestedDate}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public SetAccessedDate(
    reference: string | null
  ): AxiosPromise<Appointment> {
    return this.Post<Appointment>(
      `${this.BaseUrl}/SetAccessedDate?ref=${reference}`,
      null,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }
  
  public AppointmentIsConfirmed(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentIsConfirmed?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentCanBeCancelled(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentCanBeCancelled?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentCanBeChanged(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentCanBeChanged?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentCanBeConfirmed(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentCanBeConfirmed?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentArrivalWindow(
    extID: string | null
  ): AxiosPromise<string> {
    return this.Get(
      `${this.BaseUrl}/AppointmentArrivalWindow?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentHasNotifications(
    extID: string | null,
    notificationType: NotificationTemplateNotificationType
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentHasNotifications?extID=${extID}&notificationType=${notificationType}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentHasThoroughItems(
    extID: string | null,
    branding: Branding
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentHasThoroughItems?extID=${extID}&branding=${branding}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentHasInterimItems(
    extID: string | null,
    branding: Branding
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentHasInterimItems?extID=${extID}&branding=${branding}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentHasAGradePressureItem(
    extID: string | null,
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentHasAGradePressureItem?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentRequiresServiceEngineer(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentRequiresServiceEngineer?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }

  public AppointmentRequiresSupplementaryTest(
    extID: string | null
  ): AxiosPromise<boolean> {
    return this.Get(
      `${this.BaseUrl}/AppointmentRequiresSupplementaryTest?extID=${extID}`,
      {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      }
    );
  }
}
